import { type FC, useMemo } from 'react';

import { RenderingProvider, useCms, useContent } from '@eneco/cms';
import { useCustomerPublicGetCustomerRatings } from '@eneco/dc/hooks';
import { useLinkComponent } from '@eneco/link';
import { Box, TextLink, RatingStar, Stack, Text } from '@eneco/sparky';

import type { RatingSummaryContent } from './rating-summary-content.types';

const RatingSummaryInner: FC = () => {
  const { combineLiveDataWithTextOption, getLiveRatingDataOption, ratingNumberText, numberOfReviewsText, link, text } =
    useContent<RatingSummaryContent>();

  const { data } = useCustomerPublicGetCustomerRatings();
  const Link = useLinkComponent();

  const useLiveData = getLiveRatingDataOption;
  const showCombinedText = combineLiveDataWithTextOption;

  const rating = useLiveData ? data?.averageScore : Number(ratingNumberText);
  const ratingAmount = useLiveData ? data?.ratingsAmount : Number(numberOfReviewsText);

  if (typeof rating !== 'number') return null;

  const hasLink = link?.href && link?.text;
  const fallbackText = text ?? '';

  const reviewText = showCombinedText
    ? `${ratingAmount} ${link?.text ?? fallbackText}`
    : (link?.text ?? fallbackText);

  return (
    <Box aria-label={reviewText}>
      <Stack gap="2" alignY="start" direction="row">
        <Text size="BodyS" weight="bold" as="span">
          {`${rating.toFixed(1)}/10`}
        </Text>

        <RatingStar maxStars={5} score={rating} />

        <Text size="BodyS">
          {hasLink ? (
            <Link href={link.href} linkType={link.linktype}>
              <TextLink target={link.target}>{reviewText}</TextLink>
            </Link>
          ) : (
            reviewText
          )}
        </Text>
      </Stack>
    </Box>
  );
};

interface RatingSummaryProps {
  fields?: unknown;
  params?: unknown;
  componentName?: string;
  uid?: string;
}

const RatingSummary: FC<RatingSummaryProps> = ({ fields, params, componentName, uid }) => {
  const { adapter } = useCms();
  const rendering = useMemo(
    () => adapter.toRendering({ componentName: componentName ?? 'CustomerRating', uid, fields, params }),
    [adapter, componentName, uid, fields, params],
  );

  return (
    <RenderingProvider rendering={rendering}>
      <RatingSummaryInner />
    </RenderingProvider>
  );
};

export default RatingSummary;
